import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { DashboardNavbar } from '@/components/dashboard/DashboardNavbar';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { getDraftById, updateDraft } from '@/services/draftService';
import { FlowLinkInBio } from '@/components/booking-flow/FlowLinkInBio';
import { buildThemeFromDraft } from '@/components/draft-preview/buildThemeFromDraft';
import { toast } from 'sonner';
import {
  Loader2,
  Save,
  ExternalLink,
  Copy,
  Plus,
  Trash2,
} from 'lucide-react';
import type { FiggyDraft, DraftService, DraftCustomLink, DraftStatus } from '@/types/draft';
import { getDraftEditorPreviewShareGate } from '@/types/draft';

const STATUS_OPTIONS: { value: DraftStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'preview_ready', label: 'Preview Ready' },
  { value: 'sent', label: 'Sent' },
  { value: 'archived', label: 'Archived' },
];

const CATEGORIES = [
  'Barber', 'Stylist', 'Nail Tech', 'Lash Tech',
  'Trainer', 'Tattoo', 'Esthetician', 'Massage Therapist', 'Braider', 'Other',
];

export default function DraftEditorPage() {
  const { draftId } = useParams<{ draftId: string }>();
  const { profile } = useAuth();
  const [draft, setDraft] = useState<FiggyDraft | null>(null);
  /** Status last loaded from or written to the database — drives share actions */
  const [persistedStatus, setPersistedStatus] = useState<DraftStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load draft
  useEffect(() => {
    if (!draftId) return;
    (async () => {
      try {
        const data = await getDraftById(draftId);
        if (!data) {
          setError('Draft not found');
        } else {
          setDraft(data);
          setPersistedStatus(data.status);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load draft');
      } finally {
        setLoading(false);
      }
    })();
  }, [draftId]);

  if (profile?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  // ── Field updaters ────────────────────────────────────────────

  const setField = useCallback(
    <K extends keyof FiggyDraft>(field: K, value: FiggyDraft[K]) => {
      setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    },
    [],
  );

  const textField = (field: keyof FiggyDraft) => ({
    value: (draft?.[field] as string) || '',
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setField(field, e.target.value as any),
  });

  // ── Services CRUD ─────────────────────────────────────────────

  const addService = () => {
    if (!draft) return;
    const newService: DraftService = {
      id: crypto.randomUUID(),
      name: '',
      duration: 30,
      price: 0,
    };
    setField('services', [...draft.services, newService]);
  };

  const updateService = (id: string, patch: Partial<DraftService>) => {
    if (!draft) return;
    setField(
      'services',
      draft.services.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    );
  };

  const removeService = (id: string) => {
    if (!draft) return;
    setField(
      'services',
      draft.services.filter((s) => s.id !== id),
    );
  };

  // ── Custom links CRUD ─────────────────────────────────────────

  const addLink = () => {
    if (!draft) return;
    const newLink: DraftCustomLink = {
      id: crypto.randomUUID(),
      title: '',
      url: '',
    };
    setField('custom_links', [...draft.custom_links, newLink]);
  };

  const updateLink = (id: string, patch: Partial<DraftCustomLink>) => {
    if (!draft) return;
    setField(
      'custom_links',
      draft.custom_links.map((l) => (l.id === id ? { ...l, ...patch } : l)),
    );
  };

  const removeLink = (id: string) => {
    if (!draft) return;
    setField(
      'custom_links',
      draft.custom_links.filter((l) => l.id !== id),
    );
  };

  // ── Save ──────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!draft || !draftId) return;
    setSaving(true);
    try {
      const updated = await updateDraft(draftId, draft);
      setDraft(updated);
      setPersistedStatus(updated.status);
      toast.success('Draft saved');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Preview URL helpers ───────────────────────────────────────
  // Share actions key off persisted DB status, not unsaved local select value.

  const { previewButtonsEnabled: previewReady, needsSaveForPreview } =
    getDraftEditorPreviewShareGate(persistedStatus, draft?.status);

  const previewUrl = draft
    ? `${window.location.origin}/preview/${draft.preview_token}`
    : '';

  const copyPreviewLink = () => {
    if (!previewReady) {
      toast.error(
        needsSaveForPreview
          ? 'Save the draft before sharing — Preview Ready is not persisted yet.'
          : 'Set status to Preview Ready, then Save, before sharing the link.',
      );
      return;
    }
    navigator.clipboard.writeText(previewUrl);
    toast.success('Preview link copied');
  };

  const openPreview = () => {
    if (!previewReady) {
      toast.error(
        needsSaveForPreview
          ? 'Save the draft before opening — Preview Ready is not persisted yet.'
          : 'Set status to Preview Ready, then Save, before opening the preview.',
      );
      return;
    }
    window.open(previewUrl, '_blank');
  };

  // ── Render states ─────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <DashboardNavbar />
        <div className="pt-16 flex items-center justify-center h-[60vh]">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error || !draft) {
    return (
      <div className="min-h-screen bg-gray-50">
        <DashboardNavbar />
        <div className="pt-16 flex items-center justify-center h-[60vh]">
          <p className="text-muted-foreground">{error || 'Draft not found'}</p>
        </div>
      </div>
    );
  }

  // ── Theme for preview ─────────────────────────────────────────

  const theme = buildThemeFromDraft(draft);

  const socialLinks: Array<{ platform: string; url: string }> = [];
  if (draft.instagram_url) socialLinks.push({ platform: 'instagram', url: draft.instagram_url });
  if (draft.website_url) socialLinks.push({ platform: 'globe', url: draft.website_url });

  let handle: string | undefined;
  if (draft.instagram_handle) handle = `@${draft.instagram_handle}`;

  const displayName = draft.provider_name || draft.business_name || 'Provider';

  // ── Main layout ───────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardNavbar />
      <div className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-semibold">
                {draft.business_name || draft.provider_name || 'Untitled Draft'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Draft editor — changes are saved manually
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={copyPreviewLink}
                disabled={!previewReady}
                title={
                  previewReady
                    ? 'Copy public preview link'
                    : needsSaveForPreview
                      ? 'Save to persist Preview Ready before sharing'
                      : 'Set status to Preview Ready, then Save'
                }
              >
                <Copy className="h-3.5 w-3.5 mr-1.5" />
                Copy Link
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={openPreview}
                disabled={!previewReady}
                title={
                  previewReady
                    ? 'Open public preview'
                    : needsSaveForPreview
                      ? 'Save to persist Preview Ready before opening'
                      : 'Set status to Preview Ready, then Save'
                }
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Open Preview
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5 mr-1.5" />
                )}
                Save
              </Button>
            </div>
          </div>

          {/* Split view */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
            {/* Left: Editor form */}
            <div className="space-y-6">
              {/* Status */}
              <Card className="p-5">
                <SectionLabel>Status</SectionLabel>
                <Select
                  value={draft.status}
                  onValueChange={(val) => setField('status', val as DraftStatus)}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {needsSaveForPreview && (
                  <p className="text-xs text-amber-700 mt-2">
                    Status is Preview Ready locally — Save before the public link will work.
                  </p>
                )}
              </Card>

              {/* Identity */}
              <Card className="p-5 space-y-4">
                <SectionLabel>Identity</SectionLabel>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Business Name">
                    <Input placeholder="Studio Luxe" {...textField('business_name')} />
                  </Field>
                  <Field label="Provider Name">
                    <Input placeholder="Marcus Cole" {...textField('provider_name')} />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Category">
                    <Select
                      value={draft.category || ''}
                      onValueChange={(val) => setField('category', val)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c} value={c.toLowerCase()}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="City">
                    <Input placeholder="Los Angeles" {...textField('city')} />
                  </Field>
                </div>
                <Field label="Profile Image URL">
                  <Input placeholder="https://..." {...textField('profile_image_url')} />
                </Field>
              </Card>

              {/* Copy */}
              <Card className="p-5 space-y-4">
                <SectionLabel>Copy</SectionLabel>
                <Field label="Bio">
                  <Textarea rows={3} placeholder="Short premium bio..." {...textField('bio')} />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Hero Headline">
                    <Input placeholder="Your look, elevated." {...textField('hero_headline')} />
                  </Field>
                  <Field label="Hero Subheadline">
                    <Input placeholder="Optional subheadline" {...textField('hero_subheadline')} />
                  </Field>
                </div>
                <Field label="CTA Label">
                  <Input placeholder="Book now" {...textField('cta_label')} />
                </Field>
              </Card>

              {/* Branding */}
              <Card className="p-5 space-y-4">
                <SectionLabel>Branding</SectionLabel>
                <div className="grid grid-cols-3 gap-4">
                  <ColorField
                    label="Primary"
                    value={draft.brand_primary_color || ''}
                    onChange={(v) => setField('brand_primary_color', v || null)}
                  />
                  <ColorField
                    label="Secondary"
                    value={draft.brand_secondary_color || ''}
                    onChange={(v) => setField('brand_secondary_color', v || null)}
                  />
                  <ColorField
                    label="Accent"
                    value={draft.brand_accent_color || ''}
                    onChange={(v) => setField('brand_accent_color', v || null)}
                  />
                </div>
              </Card>

              {/* Links */}
              <Card className="p-5 space-y-4">
                <SectionLabel>Links</SectionLabel>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Instagram Handle">
                    <Input
                      placeholder="handle"
                      value={draft.instagram_handle || ''}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/^@/, '');
                        setField('instagram_handle', raw || null);
                        setField(
                          'instagram_url',
                          raw ? `https://instagram.com/${raw}` : null,
                        );
                      }}
                    />
                  </Field>
                  <Field label="Booking URL">
                    <Input placeholder="https://..." {...textField('booking_url')} />
                  </Field>
                </div>
                <Field label="Website URL">
                  <Input placeholder="https://..." {...textField('website_url')} />
                </Field>
              </Card>

              {/* Services */}
              <Card className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <SectionLabel>Services</SectionLabel>
                  <Button variant="outline" size="sm" onClick={addService}>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Add
                  </Button>
                </div>
                {draft.services.length === 0 && (
                  <p className="text-sm text-muted-foreground">No services yet.</p>
                )}
                {draft.services.map((svc) => (
                  <div
                    key={svc.id}
                    className="grid grid-cols-[1fr_80px_80px_36px] gap-2 items-end"
                  >
                    <Field label="Name">
                      <Input
                        placeholder="Haircut"
                        value={svc.name}
                        onChange={(e) => updateService(svc.id, { name: e.target.value })}
                      />
                    </Field>
                    <Field label="Min">
                      <Input
                        type="number"
                        value={svc.duration}
                        onChange={(e) =>
                          updateService(svc.id, { duration: Number(e.target.value) || 0 })
                        }
                      />
                    </Field>
                    <Field label="Price">
                      <Input
                        type="number"
                        value={svc.price}
                        onChange={(e) =>
                          updateService(svc.id, { price: Number(e.target.value) || 0 })
                        }
                      />
                    </Field>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-muted-foreground hover:text-destructive"
                      onClick={() => removeService(svc.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </Card>

              {/* Custom Links */}
              <Card className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <SectionLabel>Custom Links</SectionLabel>
                  <Button variant="outline" size="sm" onClick={addLink}>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Add
                  </Button>
                </div>
                {draft.custom_links.length === 0 && (
                  <p className="text-sm text-muted-foreground">No custom links yet.</p>
                )}
                {draft.custom_links.map((link) => (
                  <div key={link.id} className="grid grid-cols-[1fr_1fr_36px] gap-2 items-end">
                    <Field label="Title">
                      <Input
                        placeholder="My Portfolio"
                        value={link.title}
                        onChange={(e) => updateLink(link.id, { title: e.target.value })}
                      />
                    </Field>
                    <Field label="URL">
                      <Input
                        placeholder="https://..."
                        value={link.url}
                        onChange={(e) => updateLink(link.id, { url: e.target.value })}
                      />
                    </Field>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-muted-foreground hover:text-destructive"
                      onClick={() => removeLink(link.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </Card>

              {/* Internal notes */}
              <Card className="p-5 space-y-4">
                <SectionLabel>Internal Notes</SectionLabel>
                <Textarea
                  rows={3}
                  placeholder="Notes about this lead (never shown publicly)..."
                  {...textField('notes')}
                />
              </Card>
            </div>

            {/* Right: Live preview */}
            <div className="hidden lg:block">
              <div className="sticky top-20">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                  Live Preview
                </div>
                <div
                  className="bg-black rounded-[2rem] p-2 shadow-xl"
                  style={{ height: 720 }}
                >
                  <div
                    className="rounded-[1.5rem] overflow-hidden relative"
                    style={{ height: '100%' }}
                  >
                    <FlowLinkInBio
                      theme={theme}
                      businessName={draft.business_name || displayName}
                      displayName={displayName}
                      handle={handle}
                      bio={draft.bio || draft.category || undefined}
                      logoUrl={draft.profile_image_url}
                      socialLinks={socialLinks}
                      customLinks={draft.custom_links.filter((l) => l.title && l.url).map((l) => ({
                        id: l.id,
                        title: l.title,
                        url: l.url,
                      }))}
                      onBook={() => {}}
                      showBookButton={draft.services.length > 0}
                      bookButtonLabel={draft.cta_label || 'Book appointment'}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
      {children}
    </h3>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-sm font-medium mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
}) {
  return (
    <div>
      <label className="text-sm font-medium mb-1 block">{label}</label>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={value || '#5956F6'}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-9 rounded border border-border cursor-pointer p-0.5"
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#5956F6"
          className="font-mono text-xs"
        />
      </div>
    </div>
  );
}
